<template>
    <div id="purchaseAllGalleriesModal" aria-hidden="true"
         class="modal modal--rebrand fade modal-with-custom-content modal-with-custom-content--dark-background"
         role="dialog" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content" v-if="user">
                <div class="modal-body text-center">
                    <div class="gradient-icon-container">
                        <GemFilledComponent v-if="purchaseSuccessful" width="48px" height="48px"/>
                        <icon-component v-else :icon="cardIconAlt()" width="48px" height="48px"/>
                    </div>

                    <p v-if="purchaseSuccessful" class="galleryDescription">{{ successfulPurchaseMessage }}</p>

                    <template v-else>
                        <div class="galleryPrice">
                            <span>{{ totalPrice }}</span>
                            <span class="ml-1">{{ translate.product.credits }}</span>
                        </div>

                        <p class="galleryDescription">{{ purchaseDescriptionText }}</p>

                        <button-with-loading-component :disabled="purchaseLoading"
                                                       :loading="purchaseLoading"
                                                       :text="translate.product.confirmPurchase"
                                                       class="btn btn-rebrand-primary-outline btn-rebrand-sm w-100"
                                                       @click.native.prevent="purchaseAllGalleries()"
                        >
                        </button-with-loading-component>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import userMixin from "@/components/mixins/userMixin";
import privateGalleriesMixin from "@/components/mixins/gallery/privateGalleriesMixin";
import {regionRelativeUrl} from "@/core/utils/helpers/regions/regionUrlHelper";
import {NuxtRoutes} from "@/core/constants/v2/nuxtRoutes";
import IconComponent from "@/components/global/IconComponent.vue";
import GemFilledComponent from "@/components/global/icons/GemFilledComponent.vue";
import {cardIconAlt} from "@/core/constants/icons";

export default {
    components: {
        IconComponent,
        GemFilledComponent,
    },
    props: {
        user: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            purchaseLoading: false,
            purchaseSuccessful: false,
        }
    },
    computed: {
        ...mapState(['translate', 'currentUser', 'regionData']),
        isVideoUploadEnabled() {
            return !_.has(this.regionData, 'sectionsToHide.videoUpload');
        },
        unpurchasedGalleries() {
            const galleries = this.user && this.user.galleries ? this.user.galleries : [];
            return galleries.filter(g => !g.purchased);
        },
        totalPrice() {
            return this.unpurchasedGalleries.reduce((sum, g) => sum + _.get(g, 'prices.0.price', 0), 0);
        },
        totalPhotos() {
            return this.unpurchasedGalleries.reduce((sum, g) => sum + (g.photoQuantity || 0), 0);
        },
        totalVideos() {
            return this.unpurchasedGalleries.reduce((sum, g) => sum + (g.videoQuantity || 0), 0);
        },
        successfulPurchaseMessage() {
            return _.get(this, 'translate.product.galleryPurchased', '').replace(':username', _.get(this, 'user.username'));
        },
        purchaseDescriptionText() {
            const translationKey = this.isVideoUploadEnabled
                ? 'translate.product.youAreAboutToPurchaseAllGalleries'
                : 'translate.product.youAreAboutToPurchaseAllGalleriesPhotosOnly';
            return _.get(this, translationKey, '')
                .replace(':days', '7')
                .replace(':username', _.get(this, 'user.username', ''))
                .replace(':photos', this.totalPhotos)
                .replace(':videos', this.totalVideos);
        },
        creditsString() {
            return _.get(this, 'currentUser.credits', 0);
        },
        hasEnoughCredits() {
            return Number(this.creditsString) >= this.totalPrice;
        },
    },
    methods: {
        cardIconAlt,
        regionRelativeUrl,
        storeGalleryDataAndRedirect() {
            const firstGallery = this.unpurchasedGalleries[0];
            if (firstGallery) {
                const galleryData = {
                    id: firstGallery.id,
                    all: true,
                    prices: [{days: 7, price: this.totalPrice}],
                    username: this.user.username,
                };
                this.setProfileInGalleryDataToLocalStorage(galleryData);
            }

            if (_.get(this, 'currentUser.data.isManager')) {
                window.location.href = this.regionRelativeUrl('/users/products/buy-credits/');
            } else {
                window.location.href = this.regionRelativeUrl(NuxtRoutes.CHECKOUT.BUY_CREDITS);
            }
        },
        initModal() {
            this.purchaseSuccessful = false;

            setTimeout(() => {
                $(() => {
                    const element = $('#purchaseAllGalleriesModal');
                    if (element && typeof element.modal === 'function') {
                        element.modal('show');
                    }
                });
            });
        },
        async purchaseAllGalleries() {
            if (!this.hasEnoughCredits) {
                this.storeGalleryDataAndRedirect();
                return;
            }

            this.purchaseLoading = true;

            const [err, response] = await to(
                api.gallery.v2.purchaseAllGalleries(this.user.id),
            );

            this.purchaseLoading = false;

            if (_.get(response, 'data.message') === 'Not enough credits.') {
                this.storeGalleryDataAndRedirect();
                return;
            }

            if (err) {
                return toastr.error(this.translate.home.errorCommon);
            }

            this.purchaseSuccessful = true;
            this.$emit('galleryPurchased');
        },
    },
    mixins: [userMixin, privateGalleriesMixin],
    beforeDestroy() {
        window.clearModalFade();
    },
}
</script>
